import type { AttackReplicationMode } from '../types.js';
import type { MonsterGrade } from '../sim/archetypes.js';
import type { SkillApplicationV2 } from '../skill-engine/types.js';

/** Opaque, generation-safe wire identity. Never use as an array index. */
export type NetObjectId = string & { readonly __netObjectId: unique symbol };

/**
 * TARGET.1: `explicitTargetId`/`explicitPoint` are the authoritative manual
 * aim/target/point input path the contract's readiness requirements call
 * for (skill-activation-targeting-contract.md §17.4) — additive/optional so
 * a legacy client omitting them is unaffected. `explicitTargetId` is a
 * public `NetObjectId`, never an internal `enemy.id`/actor id: the server
 * resolves it and re-validates legality (relation/range/existence) before
 * it can become a locked goal — the client never gets to assert an internal
 * identity directly (review finding P1, wire-authority-boundary).
 */
export type InputAction =
  | {
      type: 'cast';
      skillSlot: number;
      explicitTargetId?: NetObjectId;
      explicitPoint?: { x: number; y: number };
    }
  | {
      /** Common movement ability. No skill slot, client position, or cooldown claims. */
      type: 'dash';
      distanceM?: number;
    };
/** Persistent basic-attack input carried by one client tick. */
export type BasicAttackCommand = 'AUTO' | 'AIM' | 'WITHHOLD' | 'START';
export type BasicAttackPhase = 'READY' | 'WINDUP' | 'ACTIVE' | 'RECOVERY';
export interface BasicAttackView {
  phase: BasicAttackPhase;
  remainingPreHitS: number;
  remainingCycleS: number;
  swingForward?: { x: number; y: number } | null;
}
export interface WeaponContactGeometryView {
  profileId: string;
  reachM?: number;
  angleDeg?: number;
  widthM?: number;
  /** The release was a thrust (a corridor along the aim), not a sweep sector. */
  thrust?: true;
  scales?: { reach: number; width: number; angle: number };
  /** Exact canonical application, including custom shapes/scales and motion. */
  application: SkillApplicationV2;
}
export interface MonsterTelegraphView {
  attack: 'CHARGE';
  phase: 'WINDUP' | 'DASH' | 'RECOVERY';
  targetNetId: NetObjectId | null;
  startedServerTick: number;
  windupTicks: number;
  direction?: { x: number; y: number } | null;
  targetPoint?: { x: number; y: number } | null;
  remainingDistanceM?: number | null;
}
export type StatusViewKind = 'dot' | 'slow' | 'stun' | 'root' | 'silence' | 'disarm' | 'heal_reduction' | 'damage_amp' | 'damage_reduction' | 'mark' | 'neutral';
/**
 * Structural wire DTO. Never spread simulation Status into this shape. Only display statuses travel: a skill
 * status, or a status bound to no other one; a bound payload child is shown by its parent.
 */
export interface StatusView {
  instanceId: number;
  definitionId: string;
  kind: StatusViewKind;
  stacks: number;
  expireServerTick: number | null;
  tags: readonly string[];
  /** The applying player's net id; absent when a monster (or nothing the room still knows) applied it. */
  ownerNetId?: NetObjectId;
  appliedServerTick: number;
  /** A skill status's own name. Every other status is named by the client from its kind or definition. */
  displayName?: string;
}

/**
 * The one table of crowd-control bits in {@link StatusSummary.cc}; the server writes them and the client reads them.
 * `frozen` is reserved for the element reader that knows a freeze parent: an ordinary root never sets it.
 */
export const STATUS_SUMMARY_CC_BITS = { stun: 1, root: 2, silence: 4, disarm: 8, frozen: 16 } as const;
export type StatusSummaryCcKind = keyof typeof STATUS_SUMMARY_CC_BITS;

/**
 * What a monster carries of the statuses its recipient did not apply: the final state only, never the statuses.
 * Every field is omitted at zero, so a monster with none of it has no summary at all (`null` on the wire).
 */
export interface StatusSummary {
  /** Bit flags of {@link STATUS_SUMMARY_CC_BITS}. */
  readonly cc?: number;
  /** The strongest active slow fraction, rounded to 1/100. */
  readonly slow?: number;
  /** Live damage-over-time instances, payload children included. */
  readonly dots?: number;
  /** Live logical skill-status or mark instances, not stacks; payload children excluded. */
  readonly marks?: number;
}

/** The complete and intentionally narrow client -> server authority boundary. */
export interface InputCommand {
  playerNetId: NetObjectId;
  sequence: number;
  /** Exactly one command is authored per client simulation tick. */
  clientTick: number;
  /** Fixed at one; variable-duration movement authority is deliberately forbidden. */
  durationTicks: 1;
  estimatedServerTick: number;
  moveX: number;
  moveY: number;
  aimAngle: number;
  actions: InputAction[];
  /** Persistent basic-attack preference; omitted preserves legacy AUTO behavior. */
  basicAttackCommand?: BasicAttackCommand;
  /** UI slots (0-3) set to automatic aim this tick; the server casts them itself. Omitted means none. */
  autoSkillSlots?: number[];
}

/** The hotbar has four slots; `autoSkillSlots` names a subset, each at most once. */
export const SKILL_SLOT_COUNT = 4;
export function validAutoSkillSlots(value: unknown): value is number[] {
  return Array.isArray(value) && value.length <= SKILL_SLOT_COUNT
    && value.every((slot, index) => Number.isSafeInteger(slot) && slot >= 0 && slot < SKILL_SLOT_COUNT && value.indexOf(slot) === index);
}

export interface PlayerSnapshot {
  kind: 'player';
  netId: NetObjectId;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  mana: number;
  maxMana?: number;
  moveSpeedMps?: number;
  movementAllowed?: boolean;
  facing?: { x: number; y: number } | null;
  weaponId?: string | null;
  basicAttack?: BasicAttackView | null;
  inputSlotMap?: readonly (number | null)[];
  /** Legacy countdown; absent when ready-tick-v1 was negotiated. */
  cooldownTicks?: readonly number[];
  /** First shared-clock tick on which cast admission can succeed. Zero means initially ready. */
  cooldownReadyTicks?: readonly number[];
  connected: boolean;
  statuses: readonly StatusView[];
  shield: number;
  down: boolean;
}

export interface MonsterSnapshot {
  kind: 'monster';
  netId: NetObjectId;
  /** Server-owned level. Present on every actual full snapshot and spawn. */
  level: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  /** The recipient's own display statuses; everyone else's travel as `statusSummary`. */
  statuses: readonly StatusView[];
  /** Others' statuses; absent when there are none. A full state is sent only where the recipient's baseline has no such object, so absence cannot leave a stale summary behind; a delta clears with `null`. */
  statusSummary?: StatusSummary | null;
  shield: number;
  vx: number;
  vy: number;
  telegraph?: MonsterTelegraphView | null;
  /** Archetype id and behaviour grade; fixed for the monster's life. Absent only from older producers. */
  archetypeId?: string;
  grade?: MonsterGrade;
}

export interface AttackSnapshot {
  kind: 'attack';
  netId: NetObjectId;
  ownerNetId: NetObjectId;
  skillId: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radiusM: number;
  replication: AttackReplicationMode;
  /** Opaque presentation profile of the releasing skill; never a file name. */
  visualProfileId: string;
  /** Authored stage that released this attack; `primary` for single-stage sources. */
  stageId: string;
  /** What the server instance is, so clients never infer it from velocity. */
  presentationKind: 'PROJECTILE' | 'AREA' | 'ORBIT';
  /** An orb only: the radius of the circle it travels on, around its owner unless `orbitCenter` is given. */
  orbitRadiusM?: number;
  orbitCenter?: { x: number; y: number };
}

export type ObjectSnapshot = PlayerSnapshot | MonsterSnapshot | AttackSnapshot;

/**
 * A field-level update carried in `updates` / `importantUpdates`. Only the
 * fields that changed against the receiver's ACK baseline are present; `kind`
 * and `netId` are always present so the receiver can locate the baseline object
 * and merge onto it. Entries in `spawns` are always full `ObjectSnapshot`s.
 */
export interface PlayerDelta {
  kind: 'player';
  netId: NetObjectId;
  x?: number;
  y?: number;
  hp?: number;
  maxHp?: number;
  mana?: number;
  maxMana?: number;
  moveSpeedMps?: number;
  movementAllowed?: boolean;
  facing?: { x: number; y: number } | null;
  weaponId?: string | null;
  basicAttack?: BasicAttackView | null;
  inputSlotMap?: readonly (number | null)[];
  cooldownTicks?: readonly number[];
  cooldownReadyTicks?: readonly number[];
  connected?: boolean;
  statuses?: readonly StatusView[];
  shield?: number;
  down?: boolean;
}

export interface MonsterDelta {
  kind: 'monster';
  netId: NetObjectId;
  level?: number;
  x?: number;
  y?: number;
  hp?: number;
  maxHp?: number;
  alive?: boolean;
  statuses?: readonly StatusView[];
  /** Omitted means unchanged; `null` clears (an omitted field cannot). */
  statusSummary?: StatusSummary | null;
  shield?: number;
  vx?: number;
  vy?: number;
  telegraph?: MonsterTelegraphView | null;
  archetypeId?: string;
  grade?: MonsterGrade;
}

/**
 * `ownerNetId`, `skillId`, `radiusM`, `replication` and the presentation
 * identity (`visualProfileId`, `stageId`, `presentationKind`) are immutable per attack
 * instance and only ever travel in the spawn full state; a steady-state attack
 * delta carries just its moving transform.
 */
export interface AttackDelta {
  kind: 'attack';
  netId: NetObjectId;
  ownerNetId?: NetObjectId;
  skillId?: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  radiusM?: number;
  replication?: AttackReplicationMode;
  visualProfileId?: string;
  stageId?: string;
  presentationKind?: 'PROJECTILE' | 'AREA' | 'ORBIT';
  orbitRadiusM?: number;
  orbitCenter?: { x: number; y: number };
}

/**
 * A partial field update merged onto the receiver's ACK baseline. Every kind is
 * delta-encoded; a full snapshot remains a structurally-valid update whose merge
 * is a complete replace, so the apply path is uniform for every element.
 */
export type ObjectUpdate = PlayerDelta | MonsterDelta | AttackDelta;

/**
 * Reconstructs a full object by merging an `ObjectUpdate` onto its ACK baseline.
 * The baseline is guaranteed present for any partial delta: the server only
 * demotes an object out of `spawns` once the client has ACKed a baseline that
 * contains its full state. Callers own cloning of the returned value.
 */
export function applyObjectUpdate(base: ObjectSnapshot | undefined, update: ObjectUpdate): ObjectSnapshot {
  const merged = (base && base.kind === update.kind ? { ...base, ...update } : { ...update }) as ObjectSnapshot;
  // `null` is how a delta clears the summary; the reconstructed state says "none" the way a full state does, by absence.
  if (merged.kind === 'monster' && merged.statusSummary === null) delete merged.statusSummary;
  return merged;
}

/**
 * Baseline retention ordering contract (Phase E.1). Two independent retained
 * sets exist on top of the ACK-diff protocol:
 *
 * - `ReplicationStream` (server, per client) retains at most one baseline per
 *   delivered tick within `SERVER_BASELINE_RETENTION_TICKS` of the newest
 *   delivered tick, **plus** the currently-acknowledged baseline itself,
 *   however old it is — it is the ACK anchor `build()` is still naming, so it
 *   is pinned rather than window-pruned. `acknowledge()` also still prunes
 *   everything strictly older than the new ACK.
 * - `PredictedClient` retains a rolling window of `CLIENT_BASELINE_RETENTION_TICKS`
 *   recent baselines, **plus** the one baseline named by the most recently
 *   *applied* delta (`baselineTick`) — pinned the same way, regardless of how
 *   long the server keeps naming it during an ACK stall.
 *
 * Because each side pins exactly the one baseline the other side is currently
 * depending on, an ACK blackout of any length cannot desync the pair: the
 * server always has the baseline the client will eventually ACK, and the
 * client always has the baseline the server is currently naming. Only a
 * *stale* ACK — for a tick the server pruned because a newer ACK already
 * moved the anchor past it — is rejected, and recovery is the next
 * (non-stale) ACK. Both windows are independently bounded and their relative
 * size is not load-bearing for correctness, only for how much rolling
 * reconstruction/interpolation history each side keeps day-to-day.
 */
export const SERVER_BASELINE_RETENTION_TICKS = 32;
export const CLIENT_BASELINE_RETENTION_TICKS = 64;

export interface ReplicationSnapshot {
  roomId: string;
  serverTick: number;
  lastProcessedInputSequence: number;
  objects: readonly ObjectSnapshot[];
}

/**
 * What an area evaluation covered. A `CIRCLE` has the event's `radiusM`; a
 * `SECTOR` has `radiusM` as its reach, `center` as its apex and opens
 * `angleDeg` about `facingRad`. `cause` says which kind of circle it was: a
 * cast's own `AREA`, the `DETONATION` a hit sets off, or one `PULSE` of a zone.
 */
export type SkillImpactShape =
  | { kind: 'CIRCLE'; cause: 'AREA' | 'DETONATION' | 'PULSE' }
  | { kind: 'SECTOR'; angleDeg: number; facingRad: number };

export type PresentationEvent =
  /** Droppable reaction flash; gameplay was already resolved by the shared runtime. */
  | { type: 'status_reaction'; tick: number; targetNetId: NetObjectId; ruleId: string; positions?: readonly { x: number; y: number }[] }
  | { type: 'cast'; tick: number; sourceId: NetObjectId; skillId: string; visualProfileId: string; castForward?: { x: number; y: number } }
  | { type: 'skill_impact'; tick: number; sourceId: NetObjectId; skillId: string; visualProfileId: string; stageId: string; center: { x: number; y: number }; radiusM: number; shape: SkillImpactShape }
  /** A ricochet or dash member going from one enemy to the next: droppable presentation; `instant` members are drawn as a short arc. */
  | { type: 'chain_link'; tick: number; sourceId: NetObjectId; skillId: string; visualProfileId: string; stageId: string; attackKey: string; from: { x: number; y: number }; to: { x: number; y: number }; instant: boolean }
  /** A planned chain's whole path in one event: consecutive points are the hops its members make, drawn as the same short arcs. */
  | { type: 'chain_path'; tick: number; sourceId: NetObjectId; skillId: string; visualProfileId: string; stageId: string; points: readonly { x: number; y: number }[]; instant: boolean }
  /**
   * A dash member moved a player. Never dropped for that player's own client, which reconciles to `to` as an accepted move
   * rather than a teleport; droppable for every other viewer. Ordered and deduplicated by `(tick, seq)`.
   */
  | { type: 'skill_displacement'; tick: number; sourceId: NetObjectId; skillId: string; stageId: string; from: { x: number; y: number }; to: { x: number; y: number }; seq: number }
  | { type: 'cast_refused'; tick: number; sourceId: NetObjectId; slot: number; skillId?: string; reason: string; status?: 'DEFERRED' | 'INVALID'; code?: string }
  | { type: 'weapon_contact'; tick: number; sourceId: NetObjectId; skillId: string; attackId?: number; origin: { x: number; y: number }; aimPoint: { x: number; y: number }; progress?: number; previousProgress?: number; durationS?: number; mode?: string; forward: { x: number; y: number }; geometry?: WeaponContactGeometryView; contacts: readonly { targetId: NetObjectId; regions?: readonly { profileId: string; regionId: string; role: 'PRIMARY' | 'SECONDARY'; fraction: number }[] }[] }
  | { type: 'monster_attack_telegraph'; tick: number; sourceId: NetObjectId; targetId: NetObjectId; attack: 'CHARGE'; windupTicks: number }
  | { type: 'monster_charge_impact'; tick: number; sourceId: NetObjectId; targetId: NetObjectId; hitPoint: { x: number; y: number } }
  | { type: 'monster_attack_cancel'; tick: number; sourceId: NetObjectId; targetId?: NetObjectId; attack: 'CHARGE' | 'PROJECTILE'; reason: 'target_lost' | 'leash' | 'death'; attackId?: NetObjectId }
  | { type: 'monster_projectile_spawn'; tick: number; sourceId: NetObjectId; targetId: NetObjectId; attackId: NetObjectId; position: { x: number; y: number }; direction: { x: number; y: number } }
  | { type: 'monster_projectile_hit'; tick: number; sourceId: NetObjectId; targetId: NetObjectId; attackId: NetObjectId; hitPoint: { x: number; y: number } }
  | { type: 'hit'; tick: number; sourceId: NetObjectId; attackId?: NetObjectId; targetId: NetObjectId; skillId: string }
  | { type: 'damage'; tick: number; sourceId?: NetObjectId; targetId: NetObjectId; skillId: string; visualProfileId: string; hpDamage: number; shieldAbsorbed: number; crit: boolean; attributes: readonly string[]; hitPoint: { x: number; y: number }; hpAfter: number }
  | { type: 'death'; tick: number; targetId: NetObjectId; sourceId?: NetObjectId }
  | { type: 'spawn'; tick: number; object: ObjectSnapshot }
  | { type: 'despawn'; tick: number; netId: NetObjectId };

export interface SimulationFrame {
  snapshot: ReplicationSnapshot;
  events: readonly PresentationEvent[];
}

export interface ConnectRequest {
  reconnectToken?: string;
}

export interface ConnectResult {
  playerNetId: NetObjectId;
  reconnectToken: string;
  snapshot: ReplicationSnapshot;
  /**
   * The room's authoritative simulation rate and player move speed. A client
   * that predicts its own movement MUST reconcile against these rather than
   * assume a default: a mismatch silently desynchronises prediction from the
   * server every tick. They are reported here so no third copy of the world
   * config's values has to be kept in sync client-side.
   */
  tickRate: number;
  playerSpeedMps: number;
}

/** ACK-baseline delta. Lifecycle entries are repeated until acknowledged. */
export interface ReplicationDelta {
  roomId: string;
  serverTick: number;
  baselineTick: number | null;
  lastProcessedInputSequence: number;
  spawns: readonly ObjectSnapshot[];
  /** Persistent semantic facts retransmitted against ACK state and never byte-budget dropped. */
  importantUpdates: readonly ObjectUpdate[];
  updates: readonly ObjectUpdate[];
  despawns: readonly NetObjectId[];
  events: readonly PresentationEvent[];
}
